import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Search, Plane, Truck, Package } from "lucide-react";
import PlaneScene from "./PlaneScene";
import { MaskedLines, Reveal } from "@/lib/motion";
import { HERO_STATS } from "@/lib/site-data";

function FloatChip({ icon: Icon, label, value, className, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.6 }}
      className={`absolute glass rounded-2xl px-4 py-2.5 flex items-center gap-3 ${className}`}
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#5A189A]">
        <Icon size={16} className="text-white" />
      </span>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[#9ca3af]">{label}</div>
        <div className="font-display text-sm font-bold text-[#1F1F1F] leading-none">{value}</div>
      </div>
    </motion.div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const globeY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden pt-[92px]">
      <div className="pointer-events-none absolute -top-24 -left-32 h-[520px] w-[520px] glow-purple" />
      <div className="pointer-events-none absolute top-1/2 right-[-6%] h-[420px] w-[420px] glow-red" />
      <div
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{ background: "radial-gradient(1200px 500px at 70% 0%, rgba(90,24,154,0.10), transparent 70%)" }}
      />

      <div className="relative z-10 mx-auto max-w-[1320px] px-5 sm:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] py-14 lg:py-20">
          <div>
            <Reveal delay={0.1}>
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#5A189A]/15 bg-[#5A189A]/5 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.18em] text-[#5A189A]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#E31B23]" />
                You Order · We Deliver · 150+ countries
              </div>
            </Reveal>

            <h1 className="font-display text-[12vw] leading-[0.95] font-extrabold text-[#1F1F1F] sm:text-6xl lg:text-[4.6rem]">
              <MaskedLines lines={["Fast Shipping To Jamaica"]} delay={0.15} />
              <MaskedLines
                lines={["Powered by XPRESSNGOSHIP LTD"]}
                delay={0.32}
                className="text-[#5A189A]"
              />
            </h1>

            <Reveal delay={0.55} className="mt-6 max-w-xl">
              <p className="text-base text-[#4b5563] leading-relaxed sm:text-lg">
                International courier, freight, cargo, e-commerce fulfilment, customs clearance and
                door-to-door delivery — 4× weekly, engineered as one seamless network.
              </p>
            </Reveal>

            <Reveal delay={0.68}>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="#tools"
                  data-testid="hero-quote-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-[#E31B23] hover:bg-[#B71319] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_12px_36px_-10px_rgba(227,27,35,0.65)]"
                >
                  Get Instant Quote
                  <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a
                  href="#tools"
                  data-testid="hero-track-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-[#1F1F1F]/15 bg-white hover:border-[#5A189A]/40 hover:text-[#5A189A] px-7 py-3.5 text-sm font-semibold text-[#1F1F1F]"
                >
                  <Search size={17} className="text-[#5A189A]" />
                  Track Shipment
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.82}>
              <div className="mt-12 grid max-w-lg grid-cols-2 gap-x-8 gap-y-6 sm:grid-cols-4">
                {HERO_STATS.map((s) => (
                  <div key={s.label}>
                    <div className="font-display text-2xl font-extrabold text-[#1F1F1F] sm:text-3xl">{s.value}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-wider text-[#9ca3af]">{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* free-floating purple cargo plane */}
          <motion.div style={{ y: globeY }} className="relative mx-auto w-full max-w-[600px]">
            <PlaneScene />

            <FloatChip icon={Plane} label="Air cargo" value="3× weekly" className="top-2 -left-2 sm:-left-4" delay={1.05} />
            <FloatChip icon={Truck} label="Door delivery" value="Same day" className="top-1/2 -right-2 sm:-right-6" delay={1.2} />
            <FloatChip icon={Package} label="Boxes shipped" value="12,480" className="-bottom-2 left-8" delay={1.35} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
